import { useEffect, useRef, useState } from 'react'

import {
  useGetUserQuery,
  useRecordCallieIntroEntryPointExposureMutation,
  useSaveCallieIntroEntryPointResponseMutation
} from 'common'
import { TRACKING_EVENT_NAMES, useTrack } from 'hooks/analytics_events'

import { DefaultOnModal } from './default_on_modal'
import { MultiSelectEntryPoint } from './multi_select_entry_point'
import { OptInModal } from './opt_in_modal'
import { SetupConfirmation } from './setup_confirmation'
import {
  type CompletionState,
  CONTROL_VARIANT,
  type CurrentUser,
  DEFAULT_ON_VARIANT,
  EXPERIMENT_KEY,
  type ExperimentVariant,
  type ExperimentState,
  isExperimentVariant,
  isTreatmentVariant,
  OPT_IN_VARIANT,
  type ResponsePayload,
  type TreatmentVariant
} from './types'

export { DefaultOnModal } from './default_on_modal'
export { MultiSelectEntryPoint } from './multi_select_entry_point'
export { OptInModal } from './opt_in_modal'
export { SetupConfirmation } from './setup_confirmation'

type CallieIntroEntryPointsProps = {
  isPersonalScope: boolean
}

type ExposedExperienceProps = {
  onComplete: (completionState: Exclude<CompletionState, null>) => void
  variant: TreatmentVariant
}

type ExperimentExposureProps = {
  hasRecordedExposure: boolean
  variant: ExperimentVariant
}

const ExperimentExposure = ({ hasRecordedExposure, variant }: ExperimentExposureProps) => {
  const didRequestExposure = useRef(false)
  const track = useTrack()
  const [recordExposure] = useRecordCallieIntroEntryPointExposureMutation()

  useEffect(() => {
    if (hasRecordedExposure || didRequestExposure.current) return

    didRequestExposure.current = true
    recordExposure().unwrap()
      .then(() => {
        track({
          name: TRACKING_EVENT_NAMES.FEATURE_FLAG_ENCOUNTERED,
          properties: {
            flag_key: EXPERIMENT_KEY,
            variation_key: variant,
            rule: 'ab_test',
            source: 'frontend'
          }
        })
      })
      .catch(() => {
        // The experience remains visible and exposure is retried the next time it mounts.
      })
  }, [hasRecordedExposure, recordExposure, track, variant])

  return null
}

const ExposedExperience = ({ onComplete, variant }: ExposedExperienceProps) => {
  const [saveResponse, { isLoading }] = useSaveCallieIntroEntryPointResponseMutation()

  const persistResponse = async (payload: ResponsePayload, enablesCallie: boolean) => {
    if (isLoading) return

    try {
      const experimentState = await saveResponse(payload).unwrap() as ExperimentState
      const showsConfirmation = enablesCallie
        && experimentState.post_intro_callie_setup_status === 'enabled'

      onComplete(showsConfirmation ? 'confirmation' : 'closed')
    } catch {
      // The shared mutation shows the standard HTTP error and leaves the experience open.
    }
  }

  if (variant === OPT_IN_VARIANT) {
    return <OptInModal isLoading={isLoading} onSave={persistResponse} />
  }

  if (variant === DEFAULT_ON_VARIANT) {
    return <DefaultOnModal isLoading={isLoading} onSave={persistResponse} />
  }

  return <MultiSelectEntryPoint isLoading={isLoading} onSave={persistResponse} />
}

export const CallieIntroEntryPoints = ({ isPersonalScope }: CallieIntroEntryPointsProps) => {
  const { data } = useGetUserQuery()
  const currentUser = data as CurrentUser | undefined
  const experimentState = currentUser?.growth_experiments?.[EXPERIMENT_KEY]
  const [completionState, setCompletionState] = useState<CompletionState>(null)

  if (completionState === 'confirmation') {
    return (
      <SetupConfirmation
        email={currentUser?.email || ''}
        onClose={() => setCompletionState('closed')}
      />
    )
  }

  if (
    completionState === 'closed'
    || !isPersonalScope
    || !isExperimentVariant(experimentState?.variant)
    || !experimentState.statsig_rule_id
  ) {
    return null
  }

  const exposure = (
    <ExperimentExposure
      hasRecordedExposure={Boolean(experimentState.post_intro_exposed_at)}
      variant={experimentState.variant}
    />
  )

  if (experimentState.variant === CONTROL_VARIANT) return exposure
  if (!isTreatmentVariant(experimentState.variant) || experimentState.post_intro_response) return null

  return (
    <>
      {exposure}
      <ExposedExperience
        onComplete={setCompletionState}
        variant={experimentState.variant}
      />
    </>
  )
}
